import React from 'react'
import { Card, CardContent, Typography, Box, Grid } from '@material-ui/core'
import {
    People as PeopleIcon,
    Mail,
    Phone,
    Home as PropertyIcon,
} from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Lead } from '../../../../models/Outreach/types'

interface LeadsStatsProps {
    leads: Lead[] // Leads array is no longer used for calculations, but kept for backwards compatibility
    leadsPagination: {
        count: number
        next: string | null
        previous: string | null
        currentPage: number
        pageSize: number
        totalPages: number
        summary_stats: {
            total_count: number
            from_email: number
            from_phone: number
            from_property: number
        } | null
    }
    isLoading?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    statsCard: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s ease',
        height: '100%',
        '&:hover': {
            boxShadow:
                '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
    },
    statsCardContent: {
        padding: theme.spacing(2),
        '&:last-child': {
            paddingBottom: theme.spacing(2),
        },
    },
    statsIcon: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: theme.spacing(1),
    },
    statsValue: {
        fontSize: '1.875rem',
        fontWeight: 'bold',
        lineHeight: 1.2,
    },
    statsValueLoading: {
        color: theme.palette.text.disabled,
    },
    statsLabel: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
        fontWeight: 400,
    },
    statsLabelLoading: {
        color: theme.palette.text.disabled,
    },
    statsCardLoading: {
        opacity: 0.4,
        backgroundColor: '#f9fafb',
        border: '1px solid #e5e7eb',
        boxShadow: 'none',
        '&:hover': {
            boxShadow: 'none',
        },
    },
    statsIconLoading: {
        opacity: 0.3,
    },
}))

export const LeadsStats: React.FC<LeadsStatsProps> = ({
    leads,
    leadsPagination,
    isLoading = false,
}) => {
    const classes = useStyles()

    // If we have cached summary stats, don't show loading state
    const hasCachedStats = !!leadsPagination.summary_stats
    const shouldShowLoading = isLoading && !hasCachedStats

    // Use summary_stats from API
    const stats = leadsPagination.summary_stats

    return (
        <Grid container spacing={2} style={{ marginBottom: 8 }}>
            <Grid item xs={6} sm={4} md={2}>
                <Card
                    className={`${classes.statsCard} ${
                        shouldShowLoading ? classes.statsCardLoading : ''
                    }`}
                >
                    <CardContent className={classes.statsCardContent}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                        >
                            <Box>
                                <Typography
                                    className={`${classes.statsLabel} ${
                                        shouldShowLoading
                                            ? classes.statsLabelLoading
                                            : ''
                                    }`}
                                >
                                    Total Leads
                                </Typography>
                                <Typography
                                    className={`${classes.statsValue} ${
                                        shouldShowLoading
                                            ? classes.statsValueLoading
                                            : ''
                                    }`}
                                    style={
                                        !shouldShowLoading
                                            ? { color: '#3b82f6' }
                                            : undefined
                                    }
                                >
                                    {shouldShowLoading
                                        ? '-'
                                        : stats?.total_count?.toLocaleString() ||
                                          0}
                                </Typography>
                            </Box>
                            <Box
                                className={`${classes.statsIcon} ${
                                    shouldShowLoading
                                        ? classes.statsIconLoading
                                        : ''
                                }`}
                                style={
                                    !shouldShowLoading
                                        ? {
                                              backgroundColor:
                                                  'rgba(59, 130, 246, 0.1)',
                                          }
                                        : {
                                              backgroundColor:
                                                  'rgba(0, 0, 0, 0.05)',
                                          }
                                }
                            >
                                <PeopleIcon
                                    style={{
                                        fontSize: 16,
                                        color: shouldShowLoading
                                            ? '#9ca3af'
                                            : '#3b82f6',
                                    }}
                                />
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
                <Card
                    className={`${classes.statsCard} ${
                        shouldShowLoading ? classes.statsCardLoading : ''
                    }`}
                >
                    <CardContent className={classes.statsCardContent}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                        >
                            <Box>
                                <Typography
                                    className={`${classes.statsLabel} ${
                                        shouldShowLoading
                                            ? classes.statsLabelLoading
                                            : ''
                                    }`}
                                >
                                    From Email
                                </Typography>
                                <Typography
                                    className={`${classes.statsValue} ${
                                        shouldShowLoading
                                            ? classes.statsValueLoading
                                            : ''
                                    }`}
                                    style={
                                        !shouldShowLoading
                                            ? { color: '#10b981' }
                                            : undefined
                                    }
                                >
                                    {shouldShowLoading
                                        ? '-'
                                        : stats?.from_email?.toLocaleString() ||
                                          0}
                                </Typography>
                            </Box>
                            <Box
                                className={`${classes.statsIcon} ${
                                    shouldShowLoading
                                        ? classes.statsIconLoading
                                        : ''
                                }`}
                                style={
                                    !shouldShowLoading
                                        ? {
                                              backgroundColor:
                                                  'rgba(16, 185, 129, 0.1)',
                                          }
                                        : {
                                              backgroundColor:
                                                  'rgba(0, 0, 0, 0.05)',
                                          }
                                }
                            >
                                <Mail
                                    style={{
                                        fontSize: 16,
                                        color: shouldShowLoading
                                            ? '#9ca3af'
                                            : '#10b981',
                                    }}
                                />
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
                <Card
                    className={`${classes.statsCard} ${
                        shouldShowLoading ? classes.statsCardLoading : ''
                    }`}
                >
                    <CardContent className={classes.statsCardContent}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                        >
                            <Box>
                                <Typography
                                    className={`${classes.statsLabel} ${
                                        shouldShowLoading
                                            ? classes.statsLabelLoading
                                            : ''
                                    }`}
                                >
                                    From Phone
                                </Typography>
                                <Typography
                                    className={`${classes.statsValue} ${
                                        shouldShowLoading
                                            ? classes.statsValueLoading
                                            : ''
                                    }`}
                                    style={
                                        !shouldShowLoading
                                            ? { color: '#f59e0b' }
                                            : undefined
                                    }
                                >
                                    {shouldShowLoading
                                        ? '-'
                                        : stats?.from_phone?.toLocaleString() ||
                                          0}
                                </Typography>
                            </Box>
                            <Box
                                className={`${classes.statsIcon} ${
                                    shouldShowLoading
                                        ? classes.statsIconLoading
                                        : ''
                                }`}
                                style={
                                    !shouldShowLoading
                                        ? {
                                              backgroundColor:
                                                  'rgba(245, 158, 11, 0.1)',
                                          }
                                        : {
                                              backgroundColor:
                                                  'rgba(0, 0, 0, 0.05)',
                                          }
                                }
                            >
                                <Phone
                                    style={{
                                        fontSize: 16,
                                        color: shouldShowLoading
                                            ? '#9ca3af'
                                            : '#f59e0b',
                                    }}
                                />
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
                <Card
                    className={`${classes.statsCard} ${
                        shouldShowLoading ? classes.statsCardLoading : ''
                    }`}
                >
                    <CardContent className={classes.statsCardContent}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                        >
                            <Box>
                                <Typography
                                    className={`${classes.statsLabel} ${
                                        shouldShowLoading
                                            ? classes.statsLabelLoading
                                            : ''
                                    }`}
                                >
                                    From Property
                                </Typography>
                                <Typography
                                    className={`${classes.statsValue} ${
                                        shouldShowLoading
                                            ? classes.statsValueLoading
                                            : ''
                                    }`}
                                    style={
                                        !shouldShowLoading
                                            ? { color: '#8b5cf6' }
                                            : undefined
                                    }
                                >
                                    {shouldShowLoading
                                        ? '-'
                                        : stats?.from_property?.toLocaleString() ||
                                          0}
                                </Typography>
                            </Box>
                            <Box
                                className={`${classes.statsIcon} ${
                                    shouldShowLoading
                                        ? classes.statsIconLoading
                                        : ''
                                }`}
                                style={
                                    !shouldShowLoading
                                        ? {
                                              backgroundColor:
                                                  'rgba(139, 92, 246, 0.1)',
                                          }
                                        : {
                                              backgroundColor:
                                                  'rgba(0, 0, 0, 0.05)',
                                          }
                                }
                            >
                                <PropertyIcon
                                    style={{
                                        fontSize: 16,
                                        color: shouldShowLoading
                                            ? '#9ca3af'
                                            : '#8b5cf6',
                                    }}
                                />
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    )
}
